import type { Locale } from './settings';

/** Tunisian conventions: Latin digits in every locale, decimal comma, no-break space as thousands separator. */
const NBSP = ' ';

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

export function formatNumber(value: number, decimals = 0): string {
  const negative = value < 0;
  const fixed = roundHalfUp(Math.abs(value), decimals).toFixed(decimals);
  const [intPart = '0', frac] = fixed.split('.');
  const out = group(intPart) + (frac ? `,${frac}` : '');
  return negative ? `-${out}` : out;
}

/** Half-up rounding that is safe for values like 1.005 or 9.995. */
export function roundHalfUp(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return (
    Math.round((value + Math.sign(value) * Number.EPSILON * Math.max(1, Math.abs(value))) * factor) / factor
  );
}

/** Grade on 20, e.g. 14.75 → "14,75". */
export function formatGrade(value: number | null | undefined, decimals = 2): string {
  return value === null || value === undefined ? '—' : formatNumber(value, decimals);
}

const CURRENCY: Record<Locale, string> = { fr: 'DT', en: 'TND', ar: 'د.ت' };

/** Amounts are stored in millimes (1 DT = 1 000 millimes): 3_250_000 → "3 250,000 DT". */
export function formatMoney(millimes: number, locale: Locale = 'fr', decimals = 3): string {
  return `${formatNumber(millimes / 1000, decimals)}${NBSP}${CURRENCY[locale]}`;
}

export function formatPercent(value: number, decimals = 1): string {
  return `${formatNumber(value, decimals)}${NBSP}%`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** "2026-09-29" or Date → "29/09/2026" (date-only strings are not shifted by the time zone). */
export function formatDate(value: string | Date): string {
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return `${m[3] ?? ''}/${m[2] ?? ''}/${m[1] ?? ''}`;
    value = new Date(value);
  }
  return `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()}`;
}

export function formatTime(value: Date): string {
  return `${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

/** Initials for avatars: "Mohamed Aziz Ben Salah" → "MA". */
export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

/**
 * Holder name for the public verification page (design ScrVerify: "Y***** T*******i"):
 * first names keep their initial, the last name its first and last letters.
 */
export function maskName(firstName: string, lastName: string): string {
  const mask = (w: string, keepLast: boolean) => {
    const chars = Array.from(w);
    if (chars.length <= 1) return w;
    if (keepLast && chars.length > 2)
      return `${chars[0] ?? ''}${'*'.repeat(chars.length - 2)}${chars.at(-1) ?? ''}`;
    return `${chars[0] ?? ''}${'*'.repeat(chars.length - 1)}`;
  };
  const first = firstName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => mask(w, false));
  const last = lastName.trim().split(/\s+/).filter(Boolean);
  const lastMasked = last.map((w, i) => mask(w, i === last.length - 1));
  return [...first, ...lastMasked].join(' ');
}
